import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Heading, Text, Button, Input, TextArea } from '@indeed/ifl-components';
import { GettextInterface } from '@indeed/indeed-gettext';
import PageTransition from '../../../components/PageTransition/PageTransition';
import JobDescription from '../../../components/JobDescription/JobDescription';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';

export interface WorkExperiencePageProps {
    step?: string;
    gt?: GettextInterface;
}

interface WorkExperience {
    company: string;
    position: string;
    startDate: string;
    endDate: string;
    isCurrentJob: boolean;
    responsibilities: string;
    achievements: string;
}

interface WorkExperienceData {
    experiences: WorkExperience[];
    skills: string;
    certifications: string;
    careerObjective: string;
}

const WorkExperiencePage: React.FunctionComponent<WorkExperiencePageProps> = () => {
    const { goBack, canGoBack, goForward } = usePageFlow();
    const [formData, setFormData] = useState<WorkExperienceData>({
        experiences: [
            {
                company: '',
                position: '',
                startDate: '',
                endDate: '',
                isCurrentJob: false,
                responsibilities: '',
                achievements: '',
            },
        ],
        skills: '',
        certifications: '',
        careerObjective: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleExperienceChange = (
        index: number,
        field: keyof WorkExperience,
        value: string | boolean,
    ): void => {
        setFormData((prev) => ({
            ...prev,
            experiences: prev.experiences.map((exp, i) =>
                i === index ? { ...exp, [field]: value } : exp,
            ),
        }));
    };

    const handleFormDataChange = (
        field: keyof Omit<WorkExperienceData, 'experiences'>,
        value: string,
    ): void => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        formData.experiences.forEach((exp, index) => {
            if (!exp.company.trim()) {
                newErrors[`company_${index}`] = '会社名は必須です';
            }
            if (!exp.position.trim()) {
                newErrors[`position_${index}`] = '職種は必須です';
            }
            if (!exp.startDate) {
                newErrors[`startDate_${index}`] = '開始日は必須です';
            }
        });

        if (!formData.careerObjective.trim()) {
            newErrors.careerObjective = 'キャリア目標は必須です';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleBack = (): void => {
        void goBack();
    };

    const handleContinue = (): void => {
        if (validateForm()) {
            void goForward();
        }
    };

    const handleSkip = (): void => {
        void goForward();
    };

    return (
        <PageTransition pageKey="work-experience">
            <Helmet>
                <title>職務経歴書 - Work Experience - Indeed</title>
                <meta
                    name="description"
                    content="詳細な職務経歴を入力して、あなたの経験とスキルをアピールしましょう。"
                />
                <meta
                    name="keywords"
                    content="職務経歴書, work experience, job history, skills, Japan, Indeed"
                />
            </Helmet>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: ['1fr', '1fr 1fr'],
                    gap: 6,
                    alignItems: 'start',
                }}
            >
                {/* Left Section - Work Experience Form */}
                <Box
                    sx={{
                        backgroundColor: 'background.default',
                        padding: 6,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'border.subtle',
                    }}
                >
                    <Heading
                        as="h2"
                        level={2}
                        sx={{
                            color: 'foreground.primary',
                            marginBlockEnd: 2,
                        }}
                    >
                        職務経歴書 (Work Experience)
                    </Heading>
                    <Text sx={{ color: 'foreground.secondary', fontSize: 2, marginBlockEnd: 6 }}>
                        あなたの職務経歴、スキル、実績を詳しく記入してください。
                    </Text>

                    {/* Career Objective */}
                    <Box sx={{ marginBlockEnd: 6 }}>
                        <Text
                            sx={{
                                color: 'foreground.primary',
                                fontWeight: 'semibold',
                                marginBlockEnd: 3,
                                display: 'block',
                            }}
                        >
                            キャリア目標 / Career Objective *
                        </Text>
                        <TextArea
                            value={formData.careerObjective}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                handleFormDataChange('careerObjective', e.target.value)
                            }
                            placeholder="あなたのキャリア目標や志望動機を記入してください..."
                            rows={3}
                            sx={{ inlineSize: '100%' }}
                        />
                        {errors.careerObjective && (
                            <Text
                                sx={{ color: 'danger.default', fontSize: 1, marginBlockStart: 2 }}
                            >
                                {errors.careerObjective}
                            </Text>
                        )}
                    </Box>

                    {/* Work Experience Entry */}
                    <Box sx={{ marginBlockEnd: 6 }}>
                        <Heading
                            as="h3"
                            level={4}
                            sx={{ color: 'foreground.primary', marginBlockEnd: 4 }}
                        >
                            職歴 / Work History
                        </Heading>

                        {formData.experiences.map((experience, index) => (
                            <Box
                                key={index}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'border.subtle',
                                    borderRadius: 2,
                                    padding: 4,
                                    marginBlockEnd: 4,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: ['1fr', '1fr 1fr'],
                                        gap: 3,
                                        marginBlockEnd: 3,
                                    }}
                                >
                                    <Box>
                                        <Text sx={{ fontSize: 1, marginBlockEnd: 2 }}>
                                            会社名 *
                                        </Text>
                                        <Input
                                            value={experience.company}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleExperienceChange(
                                                    index,
                                                    'company',
                                                    e.target.value,
                                                )
                                            }
                                            placeholder="株式会社Indeed"
                                        />
                                        {errors[`company_${index}`] && (
                                            <Text
                                                sx={{
                                                    color: 'danger.default',
                                                    fontSize: 1,
                                                    marginBlockStart: 1,
                                                }}
                                            >
                                                {errors[`company_${index}`]}
                                            </Text>
                                        )}
                                    </Box>
                                    <Box>
                                        <Text sx={{ fontSize: 1, marginBlockEnd: 2 }}>職位 *</Text>
                                        <Input
                                            value={experience.position}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleExperienceChange(
                                                    index,
                                                    'position',
                                                    e.target.value,
                                                )
                                            }
                                            placeholder="ソフトウェアエンジニア"
                                        />
                                        {errors[`position_${index}`] && (
                                            <Text
                                                sx={{
                                                    color: 'danger.default',
                                                    fontSize: 1,
                                                    marginBlockStart: 1,
                                                }}
                                            >
                                                {errors[`position_${index}`]}
                                            </Text>
                                        )}
                                    </Box>
                                </Box>

                                <Box>
                                    <Text sx={{ fontSize: 1, marginBlockEnd: 2 }}>
                                        主な業務内容
                                    </Text>
                                    <TextArea
                                        value={experience.responsibilities}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                            handleExperienceChange(
                                                index,
                                                'responsibilities',
                                                e.target.value,
                                            )
                                        }
                                        placeholder="主な業務内容や責任について記入してください..."
                                        rows={3}
                                        sx={{ inlineSize: '100%' }}
                                    />
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {/* Skills */}
                    <Box sx={{ marginBlockEnd: 6 }}>
                        <Text
                            sx={{
                                color: 'foreground.primary',
                                fontWeight: 'semibold',
                                marginBlockEnd: 3,
                                display: 'block',
                            }}
                        >
                            スキル・技術 / Skills
                        </Text>
                        <TextArea
                            value={formData.skills}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                handleFormDataChange('skills', e.target.value)
                            }
                            placeholder="プログラミング言語、フレームワーク、ツールなど..."
                            rows={3}
                            sx={{ inlineSize: '100%' }}
                        />
                    </Box>

                    {/* Navigation Buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 3,
                            marginBlockStart: 8,
                        }}
                    >
                        <Box>
                            {canGoBack && (
                                <Button variant="secondary" onClick={handleBack}>
                                    Back
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                            <Button variant="tertiary" onClick={handleSkip}>
                                Skip for Now
                            </Button>
                            <Button variant="primary" onClick={handleContinue}>
                                Continue
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* Right Section - Job Description */}
                <JobDescription />
            </Box>
        </PageTransition>
    );
};

export default WorkExperiencePage;
